import React from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const About = () => {
  return (
    <div className="about-page">
      {/* ABOUT HERO */}
      <section className="about-hero">
        <div className="hero-bg"></div>
        <div className="hero-bg-dots"></div>
        <div className="hero-blob blob1"></div>
        <div className="hero-blob blob2"></div>
        <div className="container">
          <div className="section-label">About Us</div>
          <h1>Building Workforce Solutions<br/>Since 2018</h1>
          <p>
            Krishpar Technologies is a Chennai-based HR services and technology company with over 7 years 
            of experience helping businesses hire smarter, pay accurately, and grow sustainably.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="about-story">
        <div className="container">
          <div className="story-grid">
            <div className="story-content">
              <div className="section-label">Our Story</div>
              <h2 className="section-title">From a Vision to a Full-Service HR Powerhouse</h2>
              <p>
                Founded in 2018, Krishpar Technologies was built with a simple but powerful conviction: 
                that businesses deserve better HR solutions — ones that combine deep domain expertise with modern technology.
              </p>
              <p>
                Starting with staffing and recruitment, we quickly recognised that our clients needed more than just talent — 
                they needed end-to-end workforce management. That belief led us to build our own platforms: 
                <strong>Emploi</strong> for HR management and <strong>PeopleX HR</strong> for payroll automation.
              </p>
              <p>
                Today, we're a trusted partner to organisations across industries, delivering compliant, efficient, 
                and people-first HR services from a single integrated ecosystem.
              </p>
            </div>
            <div className="timeline">
              <div className="timeline-item">
                <div className="timeline-year">2018</div>
                <div className="timeline-content">
                  <h4>Krishpar Technologies Founded</h4>
                  <p>Launched in Chennai with a focus on staffing and recruitment services for emerging businesses.</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-year">2019 – 2020</div>
                <div className="timeline-content">
                  <h4>Payroll Practice Established</h4>
                  <p>Expanded into full payroll management and statutory compliance services, serving a growing client base.</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-year">2021</div>
                <div className="timeline-content">
                  <h4>Emploi Platform Launched</h4>
                  <p>Released our flagship HRMS platform — Emploi — to digitise the entire employee lifecycle for clients.</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-year">2022</div>
                <div className="timeline-content">
                  <h4>PeopleX HR Platform Released</h4>
                  <p>Introduced our intelligent payroll automation platform to streamline payroll processing for businesses of all sizes.</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-year">2023 – Present</div>
                <div className="timeline-content">
                  <h4>Expansion & Innovation</h4>
                  <p>Growing our team, expanding service offerings, and continuously innovating to meet evolving workforce needs.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="about-values">
        <div className="container">
          <div className="section-head center">
            <div className="section-label">Our Values</div>
            <h2 className="section-title">Guiding Principles That Shape Everything We Do</h2>
            <p className="section-sub">
              Our values are not just words on a wall — they're the foundation of how we serve our clients, 
              develop our technology, and build our team.
            </p>
          </div>
          <div className="values-grid">
            <div className="value-card">
              <div className="value-emoji">🎯</div>
              <h3>Excellence in Execution</h3>
              <p>
                We deliver what we promise, with precision and reliability. Every placement, every payroll cycle, 
                every solution — executed to the highest standards.
              </p>
            </div>
            <div className="value-card">
              <div className="value-emoji">🤝</div>
              <h3>Client Partnership</h3>
              <p>
                We work as an extension of your team, not just a vendor. Your success is our success, 
                and we're committed to long-term partnerships built on trust and results.
              </p>
            </div>
            <div className="value-card">
              <div className="value-emoji">🚀</div>
              <h3>Innovation First</h3>
              <p>
                Technology is at our core. We continuously innovate to solve complex HR challenges, 
                making workforce management simpler, smarter, and more efficient.
              </p>
            </div>
            <div className="value-card">
              <div className="value-emoji">🛡️</div>
              <h3>Compliance & Integrity</h3>
              <p>
                We operate with complete transparency and adhere to the highest standards of compliance. 
                Your business is always protected with us.
              </p>
            </div>
            <div className="value-card">
              <div className="value-emoji">👥</div>
              <h3>People-Centric</h3>
              <p>
                At the heart of everything we do are people — your employees, our candidates, our team. 
                We create solutions that respect and empower everyone.
              </p>
            </div>
            <div className="value-card">
              <div className="value-emoji">📈</div>
              <h3>Growth Mindset</h3>
              <p>
                We're always learning, improving, and scaling. We bring this growth mindset to help our 
                clients achieve their business objectives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERTISE */}
      <section className="about-expertise">
        <div className="container">
          <div className="section-head">
            <div className="section-label">Our Expertise</div>
            <h2 className="section-title">Deep Domain Knowledge Across Multiple Areas</h2>
            <p className="section-sub">
              Our team brings together decades of combined experience in HR, technology, and business operations.
            </p>
          </div>
          <div className="expertise-grid">
            <div className="exp-card">
              <h3>IT Staffing & Recruitment</h3>
              <p>
                Specialized expertise in technology talent acquisition across software development, infrastructure, 
                data science, cybersecurity, and emerging technologies.
              </p>
            </div>
            <div className="exp-card">
              <h3>Payroll & Statutory Compliance</h3>
              <p>
                In-depth knowledge of Indian labour laws, PF, ESI, professional tax, GST, and other statutory 
                requirements across multiple states.
              </p>
            </div>
            <div className="exp-card">
              <h3>HR Technology Development</h3>
              <p>
                Proven capability in building scalable HR platforms that handle complex workforce scenarios 
                for businesses of all sizes.
              </p>
            </div>
            <div className="exp-card">
              <h3>Workforce Consulting</h3>
              <p>
                Strategic consulting on workforce planning, talent strategy, organizational design, and 
                HR process optimization.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="about-leadership">
        <div className="container">
          <div className="section-head center">
            <div className="section-label">Leadership Team</div>
            <h2 className="section-title">Experienced Leaders Driving Our Vision</h2>
            <p className="section-sub">
              Our leadership team brings together diverse expertise from HR, technology, and business management.
            </p>
          </div>
          <div className="leadership-grid">
            <div className="leader-card">
              <div className="leader-avatar">
                <div className="avatar-placeholder">KD</div>
              </div>
              <h3>Founder & CEO</h3>
              <p className="leader-title">Visionary leader with 15+ years in HR technology and workforce solutions</p>
            </div>
            <div className="leader-card">
              <div className="leader-avatar">
                <div className="avatar-placeholder">CT</div>
              </div>
              <h3>Chief Technology Officer</h3>
              <p className="leader-title">Technology architect driving innovation in HR platforms</p>
            </div>
            <div className="leader-card">
              <div className="leader-avatar">
                <div className="avatar-placeholder">HO</div>
              </div>
              <h3>Head of Operations</h3>
              <p className="leader-title">Operational excellence expert ensuring service quality</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Partner With Us?</h2>
            <p>
              Join hundreds of companies that trust Krishpar Technologies for their HR and workforce needs. 
              Let's discuss how we can help your business grow.
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn-primary">Get Started</Link>
              <Link to="/it-staffing-contracts" className="btn-outline">Our Services</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
